type Callback<T = unknown> = (data?: T) => void;

export abstract class EventEmitter<
  Events extends Record<string, unknown> = Record<string, unknown>,
> {
  private listeners: { [K in keyof Events]?: Callback<Events[K]>[] } = {};
  private onceListeners: { [K in keyof Events]?: Callback<Events[K]>[] } = {};

  public on<K extends keyof Events>(event: K, callback: Callback<Events[K]>): void {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  public once<K extends keyof Events>(event: K, callback: Callback<Events[K]>): void {
    if (!this.onceListeners[event]) this.onceListeners[event] = [];
    this.onceListeners[event].push(callback);
  }

  public off<K extends keyof Events>(event: K, callback: Callback<Events[K]>): void {
    this.listeners[event] = this.listeners[event]?.filter((cb) => cb !== callback);
    this.onceListeners[event] = this.onceListeners[event]?.filter((cb) => cb !== callback);
  }

  protected emit<K extends keyof Events>(event: K, data?: Events[K]): void {
    for (const cb of this.listeners[event] || []) {
      try {
        cb(data);
      } catch (e) {
        console.error(`Error in "${String(event)}" listener:`, e);
      }
    }

    for (const cb of this.onceListeners[event] || []) {
      try {
        cb(data);
      } catch (e) {
        console.error(`Error in once "${String(event)}" listener:`, e);
      }
    }

    this.onceListeners[event] = [];
  }

  public clear<K extends keyof Events>(event?: K): void {
    if (event) {
      delete this.listeners[event];
      delete this.onceListeners[event];
    } else {
      this.listeners = {};
      this.onceListeners = {};
    }
  }
}
